/**
 * Scan resolve ของป้าย Kanban การผลิต (`OUTPUT_TAG`) — `POST /v1/shop-floor/scan/resolve`
 * (D-170, `docs/API_CONTRACT.md` ย่อหน้า "Scan resolve — ชนิดที่ห้า OUTPUT_TAG")
 *
 * หลักการเดียวกับ `lib/outputTags/client.ts`:
 * - ไม่ throw ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้ (`ready` / failure)
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ไม่พบป้าย"
 * - ไม่ใส่ credential ใน URL — ใช้ `apiFetch` (เติม `Authorization` ให้เอง)
 *
 * ⚠️ นี่คือการ**อ่าน**อย่างเดียว ไม่เข้าคิวออฟไลน์ — เครื่องต้องออนไลน์เพื่อเรียก
 * endpoint นี้ (หน้าจอเป็นผู้เช็ค `useConnectivity()` ก่อนเรียก)
 */

import { apiFetch } from '../auth/authorizedFetch';
import { OUTPUT_TAG_CODE_PATTERN } from '../outputTags/types';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const SCAN_RESOLVE_PATH = '/v1/shop-floor/scan/resolve';


/** QR เต็มของป้าย: `CAPTAG:<schema_version>:<tag_kind>:<tag_code>` */
const QR_PATTERN = /^CAPTAG:(\d+):([A-Z_]+):(.+)$/;

export type ParsedTagCode =
  | { readonly ok: true; readonly tagCode: string; readonly schemaVersion: number }
  | { readonly ok: false; readonly message: string };

/**
 * แยกสิ่งที่สแกน/พิมพ์มาเป็น `tag_code` + `schema_version`
 *
 * รับทั้ง QR เต็ม (`CAPTAG:1:OUTPUT_TAG:OUT-00000012`) และรหัสเปล่า
 * (`KB-P1-2611A-00001` / `OUT-00000012` — หัวอ่านบางรุ่นพิมพ์เฉพาะรหัส) รูปอื่นถูกปฏิเสธด้วยข้อความไทย
 * **ก่อน**เรียก API เสมอ (acceptance: "สตริงอื่นแสดงข้อผิดพลาดไทยโดยไม่เรียก API")
 */
export function parseScannedTagCode(raw: string): ParsedTagCode {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return { ok: false, message: 'ยังไม่ได้สแกนหรือพิมพ์รหัสป้าย' };
  }

  if (OUTPUT_TAG_CODE_PATTERN.test(trimmed)) {
    return { ok: true, tagCode: trimmed, schemaVersion: 1 };
  }

  const match = QR_PATTERN.exec(trimmed);
  if (match === null) {
    return {
      ok: false,
      message:
        'รูปแบบนี้ไม่ใช่ป้าย Kanban — ต้องเป็น QR ของป้าย (CAPTAG:...) หรือรหัสป้าย เช่น KB-P1-2611A-00001 (หรือแบบเดิม OUT-nnnnnnnn)',
    };
  }

  const [, versionRaw, tagKind, tagCode] = match;
  if (tagKind !== 'OUTPUT_TAG') {
    return {
      ok: false,
      message: `ป้ายชนิด ${tagKind} ยังไม่รองรับในหน้าจอนี้ — ต้องเป็นป้าย Kanban (OUTPUT_TAG)`,
    };
  }
  if (!OUTPUT_TAG_CODE_PATTERN.test(tagCode)) {
    return { ok: false, message: 'รหัสป้ายในคิวอาร์โค้ดนี้ไม่ใช่รูปแบบรหัสป้าย (เช่น KB-P1-2611A-00001 หรือ OUT-nnnnnnnn)' };
  }

  return { ok: true, tagCode, schemaVersion: Number.parseInt(versionRaw, 10) };
}

export const OUTPUT_TAG_NEXT_ACTIONS = [
  'WAIT_MATERIAL',
  'SCAN_IN',
  'SCAN_OUT',
  'DONE',
  'NOT_ACTIVE',
  'ORDER_NOT_RUNNABLE',
] as const;
export type OutputTagNextAction = (typeof OUTPUT_TAG_NEXT_ACTIONS)[number];

export interface ScanResolveMaterial {
  readonly product_code: string;
  readonly remaining_qty: string;
}

export interface ScanResolveOutputTag {
  readonly tag_code: string;
  readonly status: string;
  readonly stage: string;
  readonly package_seq: number;
  readonly package_count: number;
  readonly tag_qty: string;
  readonly lot_no: string | null;
  readonly production_order_id: string;
  readonly order_no: string;
  readonly operation_seq: number;
  readonly work_center_code: string | null;
  readonly part_no: string;
  readonly materials_ready: boolean;
  readonly materials: readonly ScanResolveMaterial[];
  readonly next_action: OutputTagNextAction;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = { error_code: null, message: null, details: {} };

/** ความล้มเหลวทุกแบบแยกเป็นสถานะของตัวเอง — หลักการเดียวกับ `OutputTagFailure` */
export type ScanResolveFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type ScanResolveResult =
  | { readonly status: 'ready'; readonly outputTag: ScanResolveOutputTag }
  | ScanResolveFailure;

export interface ScanResolveRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ — default คือ `apiFetch` เหมือน client อื่นทุกตัว */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** nullable ได้ — `undefined` แปลว่าผิดชนิด (ต่างจาก `null` ที่เป็นค่าจริง) */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

function positiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

function requiredBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function isNextAction(value: unknown): value is OutputTagNextAction {
  return (
    typeof value === 'string' &&
    (OUTPUT_TAG_NEXT_ACTIONS as readonly string[]).includes(value)
  );
}

function parseMaterial(raw: unknown): ScanResolveMaterial | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const productCode = requiredString(record.product_code);
  const remainingQty = requiredString(record.remaining_qty);
  if (productCode === null || remainingQty === null) {
    return null;
  }
  return { product_code: productCode, remaining_qty: remainingQty };
}

/**
 * parser เข้มของ `output_tag` ในคำตอบของ `/v1/shop-floor/scan/resolve` —
 * ค่าผิดชนิด/ผิดรูปใดก็ตามคือ `null` (invariant: "parser เข้มของ output_tag")
 */
export function parseScanResolveOutputTag(raw: unknown): ScanResolveOutputTag | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const envelope = raw as Record<string, unknown>;
  const rawTag = envelope.output_tag;
  if (typeof rawTag !== 'object' || rawTag === null || Array.isArray(rawTag)) {
    return null;
  }
  const record = rawTag as Record<string, unknown>;

  const tagCode = requiredString(record.tag_code);
  const status = requiredString(record.status);
  const stage = requiredString(record.stage);
  const packageSeq = positiveInteger(record.package_seq);
  const packageCount = positiveInteger(record.package_count);
  const tagQty = requiredString(record.tag_qty);
  const productionOrderId = requiredString(record.production_order_id);
  const orderNo = requiredString(record.order_no);
  const operationSeq = positiveInteger(record.operation_seq);
  const partNo = requiredString(record.part_no);
  const materialsReady = requiredBoolean(record.materials_ready);
  const nextAction = record.next_action;

  if (
    tagCode === null ||
    status === null ||
    stage === null ||
    packageSeq === null ||
    packageCount === null ||
    tagQty === null ||
    productionOrderId === null ||
    orderNo === null ||
    operationSeq === null ||
    partNo === null ||
    materialsReady === undefined ||
    !isNextAction(nextAction)
  ) {
    return null;
  }

  const lotNo = nullableString(record.lot_no);
  const workCenterCode = nullableString(record.work_center_code);
  if (lotNo === undefined || workCenterCode === undefined) {
    return null;
  }

  const rawMaterials = record.materials;
  if (!Array.isArray(rawMaterials)) {
    return null;
  }
  const materials: ScanResolveMaterial[] = [];
  for (const entry of rawMaterials) {
    const parsed = parseMaterial(entry);
    if (parsed === null) {
      return null;
    }
    materials.push(parsed);
  }

  return {
    tag_code: tagCode,
    status,
    stage,
    package_seq: packageSeq,
    package_count: packageCount,
    tag_qty: tagQty,
    lot_no: lotNo,
    production_order_id: productionOrderId,
    order_no: orderNo,
    operation_seq: operationSeq,
    work_center_code: workCenterCode,
    part_no: partNo,
    materials_ready: materialsReady,
    materials,
    next_action: nextAction,
  };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    details,
  };
}

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return EMPTY_ENVELOPE;
  }
}

/* ------------------------------------------------------------- request --- */

/**
 * `POST /v1/shop-floor/scan/resolve` ด้วย `tag_kind=OUTPUT_TAG` — อ่านอย่างเดียว
 * ไม่ให้สิทธิ์ (Scan-In/Scan-Out ใช้สิทธิ์ของตัวเองเหมือนเดิม)
 */
export async function resolveScan(
  tagCode: string,
  schemaVersion: number,
  options: ScanResolveRequestOptions = {},
): Promise<ScanResolveResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, SCAN_RESOLVE_PATH);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tag_kind: 'OUTPUT_TAG',
        schema_version: schemaVersion,
        tag_code: tagCode,
      }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { status: 'invalid-response' };
  }

  const outputTag = parseScanResolveOutputTag(payload);
  return outputTag === null ? { status: 'invalid-response' } : { status: 'ready', outputTag };
}
